import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const FAQS = [
  {
    q: "How is SpaceCost different from FlipCost?",
    a: "FlipCost is built for whole-property renovations and flips — exterior, systems, soft costs, profit and feasibility. SpaceCost is laser-focused on the line-by-line cost of one space, no valuation, no flip math. Buy the tool that matches your scope.",
  },
  {
    q: "What does R180 actually unlock?",
    a: "One project containing up to 3 rooms — for example a kitchen + scullery + adjacent bathroom, or a primary suite. Once-off payment, no subscription. Need more rooms in the same project? Buy a +3 Room Pack for R80 any time.",
  },
  {
    q: "Can I have more than 3 rooms in one project?",
    a: "Yes. Buy a +3 Room Pack (R80, once-off) from inside the project's Rooms tab when you hit the cap. Packs stack — buy as many as you need.",
  },
  {
    q: "Can I have multiple SpaceCost projects?",
    a: "Yes — each new project is a fresh R180 unlock. Switch between them from your dashboard.",
  },
  {
    q: "Can I use the same login as FlipCost?",
    a: "Yes — SpaceCost shares your MyPlanCost account. Log in once and switch between FlipCost and SpaceCost projects from the dashboard tabs.",
  },
  {
    q: "Which currencies are supported?",
    a: "ZAR (the merchant default), USD, EUR, GBP, AUD, NZD and CAD for cost rates. Paystack billing is currently ZAR-only — payments are taken in ZAR. We display project totals in your chosen currency.",
  },
  {
    q: "Are my numbers private?",
    a: "Always. Projects are private to your account by default. Sharing is opt-in per project, with a clear view-only or edit role for each invitee.",
  },
  {
    q: "Can I share a project with my contractor?",
    a: "Yes — invite by email. They'll receive a Resend-delivered invite. You choose whether they can edit or just view.",
  },
  {
    q: "Can I export a PDF for the contractor pack?",
    a: "Yes. From the Overview tab click \"Generate Room Budget Report (PDF)\" — it produces a clean, print-ready document with the room totals, category breakdowns and (in Actual mode) variance.",
  },
  {
    q: "Are the prices accurate for my country?",
    a: "Defaults are smart starting points scaled to your currency. Every line item, room rate and add-on is editable in the Cost Library — save your local rates per project for full accuracy.",
  },
  {
    q: "What's coming next?",
    a: "Pro and Unlimited subscription tiers (multiple projects, branded reports, team seats), and project templates by space type. Join the notify list on the Pricing page.",
  },
  {
    q: "Refunds?",
    a: "If something goes wrong on the tech side, we'll always make it right. Beyond that, projects are once-off digital unlocks — please make sure SpaceCost is the right tool before you buy. The Live Demo on the home page is exactly the same UI.",
  },
];

export default function SpaceCostFaq() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in" data-theme="spacecost" data-testid="spacecost-faq">
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <button
            data-testid="faq-back"
            onClick={() => navigate("/spacecost")}
            className="inline-flex items-center gap-2 text-[13px] text-[#57534E] hover:text-[#1C1C1C]"
          >
            <ArrowLeft className="w-4 h-4" /> SpaceCost
          </button>
          <div className="flex items-baseline gap-2.5">
            <span className="fc-heading text-xl sm:text-2xl font-extrabold tracking-tight text-[#1C1C1C] leading-none">SpaceCost</span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              by MyPlanCost
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-20">
        <div className="fc-label text-sage-700">FAQ</div>
        <h1 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3 leading-[1.05]">
          Common questions, sharp answers.
        </h1>

        <div className="mt-10 space-y-4" data-testid="faq-list">
          {FAQS.map((f) => (
            <details key={f.q} className="fc-card p-5 group" data-testid="faq-item">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
                <span className="fc-heading text-[14px] text-[#1C1C1C]">{f.q}</span>
                <span className="text-sage-700 text-xs uppercase tracking-[0.2em]">read</span>
              </summary>
              <p className="text-[13px] text-[#57534E] leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Button
            data-testid="faq-cta-register"
            onClick={() => navigate("/auth?mode=register&product=spacecost")}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5"
          >
            Start a project <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button
            data-testid="faq-cta-pricing"
            onClick={() => navigate("/spacecost/pricing")}
            variant="ghost"
            className="text-[#57534E] hover:text-[#1C1C1C]"
          >
            View pricing
          </Button>
        </div>
      </main>
    </div>
  );
}
